import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, ChevronLeft, Info, Loader2, Target, TrendingUp, X } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

// ============ MARKETING CONTENT PAGE ============

const MARKETING_CONTENT_TYPES = [
  { value: "blog_post", label: "Legal Blog Post", desc: "SEO-optimized blog content" },
  { value: "social_media", label: "Social Media", desc: "Platform-specific posts" },
  { value: "newsletter", label: "Client Newsletter", desc: "Monthly/quarterly updates" },
  { value: "bio", label: "Attorney Bio", desc: "Professional biography" },
  { value: "testimonial_request", label: "Testimonial Request", desc: "Request client reviews" },
  { value: "case_result", label: "Case Result Showcase", desc: "Highlight wins" },
  { value: "landing_page", label: "Landing Page", desc: "Conversion-focused content" }
];

const MarketingContentPage = () => {
  const [form, setForm] = useState({
    content_type: 'blog_post',
    practice_areas: [],
    firm_name: '',
    attorney_name: '',
    target_audience: '',
    tone: 'professional',
    topic: '',
    keywords: [],
    platform: 'linkedin'
  });
  const [newKeyword, setNewKeyword] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const addKeyword = () => { if (newKeyword.trim()) { setForm(f => ({...f, keywords: [...f.keywords, newKeyword.trim()]})); setNewKeyword(''); }};
  
  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/marketing/generate`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="marketing-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-600 to-rose-600 flex items-center justify-center">
              <TrendingUp className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Marketing Content</h1>
              <p className="text-muted-foreground">Generate professional marketing content for your practice</p>
            </div>
          </div>
        </div>
        
        {!result ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>Content Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Content Type</Label>
                      <Select value={form.content_type} onValueChange={v => setForm(f => ({...f, content_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MARKETING_CONTENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Tone</Label>
                      <Select value={form.tone} onValueChange={v => setForm(f => ({...f, tone: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="approachable">Approachable</SelectItem>
                          <SelectItem value="authoritative">Authoritative</SelectItem>
                          <SelectItem value="empathetic">Empathetic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {form.content_type === 'social_media' && (
                    <div className="space-y-1.5">
                      <Label>Platform</Label>
                      <Select value={form.platform} onValueChange={v => setForm(f => ({...f, platform: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="linkedin">LinkedIn</SelectItem>
                          <SelectItem value="twitter">Twitter/X</SelectItem>
                          <SelectItem value="facebook">Facebook</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Firm Name</Label>
                      <Input placeholder="Smith & Associates" value={form.firm_name} onChange={e => setForm(f => ({...f, firm_name: e.target.value}))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Attorney Name (for bios)</Label>
                      <Input placeholder="Jane Smith, Esq." value={form.attorney_name} onChange={e => setForm(f => ({...f, attorney_name: e.target.value}))} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Topic/Subject</Label>
                    <Input placeholder="e.g., Recent changes in employment law" value={form.topic} onChange={e => setForm(f => ({...f, topic: e.target.value}))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Target Audience</Label>
                    <Input placeholder="e.g., Small business owners, accident victims" value={form.target_audience} onChange={e => setForm(f => ({...f, target_audience: e.target.value}))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>SEO Keywords</Label>
                    <div className="flex gap-2">
                      <Input placeholder="Add keyword..." value={newKeyword} onChange={e => setNewKeyword(e.target.value)} onKeyDown={e => e.key === 'Enter' && addKeyword()} />
                      <Button onClick={addKeyword} variant="outline">Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {form.keywords.map((kw, i) => <Badge key={i} variant="secondary">{kw} <button onClick={() => setForm(f => ({...f, keywords: f.keywords.filter((_, idx) => idx !== i)}))} className="ml-1">×</button></Badge>)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-gradient-to-br from-pink-600 to-rose-600 text-white border-0">
              <CardContent className="p-6">
                <Button className="w-full h-14 text-lg gap-2 bg-white text-pink-700 hover:bg-white/90" onClick={generate} disabled={loading}>
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Generating...</> : <><TrendingUp className="h-5 w-5" /> Generate Content</>}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <Card className="bg-gradient-to-r from-pink-600 to-rose-600 text-white border-0">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold">{result.content_name}</h2>
                <p className="text-white/80">{result.firm_name} • {result.tone}</p>
                <Button variant="outline" onClick={() => setResult(null)} className="mt-4 text-white border-white/20">New Content</Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.content}</div>
              </CardContent>
            </Card>
            
            {result.seo_tips && (
              <Card>
                <CardHeader><CardTitle>SEO Tips</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-1">{result.seo_tips.map((tip, i) => <li key={i} className="text-sm flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />{tip}</li>)}</ul>
                </CardContent>
              </Card>
            )}
            
            <Alert><Info className="h-4 w-4" /><AlertDescription className="text-xs">{result.disclaimer}</AlertDescription></Alert>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default MarketingContentPage;
